// ============================================================
//  src/admin/api/adminClient.js
// ============================================================
import axios from 'axios';
import { ADMIN_BASE } from '../adminConfig';


// ── Axios Instance ─────────────────────────────────────────
const adminClient = axios.create({
  baseURL: process.env.REACT_APP_API_URL,
  headers: { 'Content-Type': 'application/json' },
});

// ── Request Interceptor: attach token + admin user ─────────
adminClient.interceptors.request.use((config) => {
  const adminUser = JSON.parse(localStorage.getItem('adminUser') || '{}');
  const token = localStorage.getItem('adminToken');
  if (token) {
    config.headers['Authorization'] = `Token ${token}`;
  }
  if (adminUser?.username) {
    config.headers['X-Admin-User'] = adminUser.username;
  }
  return config;
});

// ── Response Interceptor: 401 → redirect to admin login ───
adminClient.interceptors.response.use(
  (res) => res,
  (err) => {
    const isLoginEndpoint = err.config?.url?.includes('/admin/login'); // ← PHP API path, keep as /admin/
    if (err.response?.status === 401 && !isLoginEndpoint) {
      localStorage.removeItem('adminToken');
      localStorage.removeItem('adminUser');
      window.location.href = `${ADMIN_BASE}/login`; // ← React route, updated
    }
    return Promise.reject(err);
  },
);

// ════════════════════════════════════════════════════════════
//  Auth
// ════════════════════════════════════════════════════════════
export const postAdminLogin  = (data) => adminClient.post('/admin/login/', data);   // ← PHP API, keep
export const postAdminLogout = ()     => adminClient.post('/admin/logout/', {});    // ← PHP API, keep

// ════════════════════════════════════════════════════════════
//  Dashboard
// ════════════════════════════════════════════════════════════
export const getAdminDashboard  = () => adminClient.get('/admin/dashboard/');
export const getGeneralAnalysis = ()       => adminClient.get('/admin/general-analysis/');
export const getSaleAnalysis    = (params) => adminClient.get('/admin/sale-analysis/', { params });
export const getWalletBalance   = () => adminClient.get('/admin/wallet-balance/');

// ════════════════════════════════════════════════════════════
//  Subscribers
// ════════════════════════════════════════════════════════════
export const getSubscribers        = (limit = 1000) => adminClient.get(`/admin/subscribers/?limit=${limit}`);
export const getSubscriberDetails  = (id)           => adminClient.get(`/admin/subscriber/details/?id=${id}`);
export const postCreditDebit       = (data)         => adminClient.post('/admin/subscriber/credit-debit/', data);
export const postBlockSubscriber   = (data)         => adminClient.post('/admin/subscriber/block/', data);
export const postDeleteSubscriber  = (data)         => adminClient.post('/admin/subscriber/delete/', data);
export const postCreateSubscriber  = (data)         => adminClient.post('/admin/subscriber/create/', data);
export const postUpdateSubscriber  = (data)         => adminClient.post('/admin/subscriber/update/', data);
export const postUpdateSubPassword = (data)         => adminClient.post('/admin/subscriber/update-password/', data);
export const postResetApiKey       = (data)         => adminClient.post('/admin/subscriber/reset-api-key/', data);
export const postSendEmail         = (data)         => adminClient.post('/admin/subscriber/send-email/', data);

// ════════════════════════════════════════════════════════════
//  Transactions
// ════════════════════════════════════════════════════════════
export const getAdminTransactions = (limit = 1000) => adminClient.get(`/admin/transactions/?limit=${limit}`);
export const getAdminTxDetails    = (id)           => adminClient.get(`/admin/transaction/details/?ref=${id}`);
export const postUpdateTxStatus   = (data)         => adminClient.post('/admin/transaction/update-status/', data);

// ════════════════════════════════════════════════════════════
//  Notifications
// ════════════════════════════════════════════════════════════
export const getAdminNotifications    = ()     => adminClient.get('/admin/notifications/');
export const postAddNotification      = (data) => adminClient.post('/admin/notification/add/', data);
export const postDeleteNotification   = (data) => adminClient.post('/admin/notification/delete/', data);
export const postUpdateNotifStatus    = (data) => adminClient.post('/admin/notification/update-status/', data);
export const postEditNotification     = (data) => adminClient.post('/admin/notification/edit/', data);
export const postSendPushNotification = (data) => adminClient.post('/admin/notification/send-push/', data);
export const postSendPushToUser       = (data) => adminClient.post('/admin/notification/send-push-to-user/', data);
export const postSendAllEmail         = (data) => adminClient.post('/admin/subscriber/send-all-email/', data);
export const getPushNotificationLogs  = ()     => adminClient.get('/admin/notification/push-logs/');

// ════════════════════════════════════════════════════════════
//  Support
// ════════════════════════════════════════════════════════════
export const getAdminSupportTickets  = ()     => adminClient.get('/admin/support/tickets/');
export const getAdminSupportTicket   = (id)   => adminClient.get(`/admin/support/view/?id=${id}`);
export const postAdminSupportReply   = (data) => adminClient.post('/admin/support/reply/', data);

// ════════════════════════════════════════════════════════════
//  Site / App Settings
// ════════════════════════════════════════════════════════════
export const getSiteSettings           = ()     => adminClient.get('/admin/site-settings/');
export const postUpdateSiteSettings    = (data) => adminClient.post('/admin/site-settings/update/', data);
export const postUpdateSiteStyle       = (data) => adminClient.post('/admin/site-style/update/', data);
export const getContactSettings        = ()     => adminClient.get('/admin/contact-settings/');
export const postUpdateContactSettings = (data) => adminClient.post('/admin/contact-settings/update/', data);

// ════════════════════════════════════════════════════════════
//  Networks
// ════════════════════════════════════════════════════════════
export const getAdminNetworks  = ()     => adminClient.get('/admin/networks/');
export const postUpdateNetwork = (data) => adminClient.post('/admin/network/update/', data);

// ════════════════════════════════════════════════════════════
//  Data Plans
// ════════════════════════════════════════════════════════════
export const getAdminDataPlans  = ()     => adminClient.get('/admin/data-plans/');
export const postAddDataPlan    = (data) => adminClient.post('/admin/data-plan/add/', data);
export const postUpdateDataPlan = (data) => adminClient.post('/admin/data-plan/update/', data);
export const postDeleteDataPlan = (data) => adminClient.post('/admin/data-plan/delete/', data);
export const getAdminDataTypes  = ()     => adminClient.get('/admin/data-types/');
export const postAddDataType    = (data) => adminClient.post('/admin/data-type/add/', data);
export const postUpdateDataType = (data) => adminClient.post('/admin/data-type/update/', data);
export const postDeleteDataType = (data) => adminClient.post('/admin/data-type/delete/', data);
export const postToggleDataType = (data) => adminClient.post('/admin/data-type/toggle/', data);

// ════════════════════════════════════════════════════════════
//  Cable Plans
// ════════════════════════════════════════════════════════════
export const getAdminCablePlans  = ()     => adminClient.get('/admin/cable-plans/');
export const postAddCablePlan    = (data) => adminClient.post('/admin/cable-plan/add/', data);
export const postUpdateCablePlan = (data) => adminClient.post('/admin/cable-plan/update/', data);
export const postDeleteCablePlan = (data) => adminClient.post('/admin/cable-plan/delete/', data);

// ════════════════════════════════════════════════════════════
//  Electricity Bills
// ════════════════════════════════════════════════════════════
export const getAdminElectricityBills  = ()     => adminClient.get('/admin/electricity-bills/');
export const postUpdateElectricityBill = (data) => adminClient.post('/admin/electricity-bill/update/', data);

// ════════════════════════════════════════════════════════════
//  Upgrade Requests (KYC)
// ════════════════════════════════════════════════════════════
export const getUpgradeRequests       = (status) => adminClient.get('/admin/upgrade-requests/' + (status ? `?status=${status}` : ''));
export const getUpgradeRequestDetails = (id)     => adminClient.get(`/admin/upgrade-request/details/?id=${id}`);
export const postApproveUpgrade       = (data)   => adminClient.post('/admin/upgrade-request/approve/', data);
export const postRejectUpgrade        = (data)   => adminClient.post('/admin/upgrade-request/reject/', data);

// ════════════════════════════════════════════════════════════
//  System Users
// ════════════════════════════════════════════════════════════
export const getSystemUsers         = ()     => adminClient.get('/admin/system-users/');
export const postCreateSystemUser   = (data) => adminClient.post('/admin/system-user/create/', data);
export const postUpdateSystemUser   = (data) => adminClient.post('/admin/system-user/update/', data);
export const postToggleSystemUser   = (data) => adminClient.post('/admin/system-user/toggle/', data);

// ════════════════════════════════════════════════════════════
//  Ads Banners
// ════════════════════════════════════════════════════════════
export const getAdsBanners             = ()     => adminClient.get('/admin/ads-banners/');
export const postAddAdsBanner          = (data) => adminClient.post('/admin/ads-banner/add/', data);
export const postDeleteAdsBanner       = (data) => adminClient.post('/admin/ads-banner/delete/', data);
export const postUpdateAdsBannerStatus = (data) => adminClient.post('/admin/ads-banner/update-status/', data);

// ════════════════════════════════════════════════════════════
//  Airtime Discount & Recharge-Pin Discount
// ════════════════════════════════════════════════════════════
export const getAirtimeDiscounts       = ()     => adminClient.get('/admin/airtime-discount/');
export const postAddAirtimeDiscount    = (data) => adminClient.post('/admin/airtime-discount/add/', data);
export const postUpdateAirtimeDiscount = (data) => adminClient.post('/admin/airtime-discount/update/', data);

export const getRechargePinDiscounts = ()     => adminClient.get('/admin/recharge-pin-discount/');
export const postAddRPDiscount       = (data) => adminClient.post('/admin/recharge-pin-discount/add/', data);
export const postUpdateRPDiscount    = (data) => adminClient.post('/admin/recharge-pin-discount/update/', data);

// ════════════════════════════════════════════════════════════
//  Cashback Config
// ════════════════════════════════════════════════════════════
export const getCashbackConfig         = ()     => adminClient.get('/admin/cashback-config/');
export const postUpdateCashbackConfig  = (data) => adminClient.post('/admin/cashback-config/update/', data);
export const postUpdateCashbackService = (data) => adminClient.post('/admin/cashback-config/update-service/', data);

// ════════════════════════════════════════════════════════════
//  Tier Configuration
// ════════════════════════════════════════════════════════════
export const getTierConfigs             = ()     => adminClient.get('/admin/tier-configs/');
export const postUpdateTierLimits       = (data) => adminClient.post('/admin/tier-limits/update/', data);
export const postUpdateTierRequirements = (data) => adminClient.post('/admin/tier-requirements/update/', data);

// ════════════════════════════════════════════════════════════
//  KYC Settings
// ════════════════════════════════════════════════════════════
export const getKycSettings        = ()     => adminClient.get('/admin/kyc-settings/');
export const postUpdateKycSettings = (data) => adminClient.post('/admin/kyc-settings/update/', data);

// ════════════════════════════════════════════════════════════
//  API Config
// ════════════════════════════════════════════════════════════
export const getApiConfig              = ()     => adminClient.get('/admin/api-config/');
export const postAddApiProvider        = (data) => adminClient.post('/admin/api-config/add-provider/', data);
export const postUpdateApiConfig       = (data) => adminClient.post('/admin/api-config/update/', data);
export const postDeleteApiProvider     = (data) => adminClient.post('/admin/api-config/delete-provider/', data);
export const postUpdateApiProvider     = (data) => adminClient.post('/admin/api-config/update-provider/', data);
export const getPalmPayConfig          = ()     => adminClient.get('/admin/palmpay-config/');
export const postUpdatePalmPayConfig   = (data) => adminClient.post('/admin/palmpay-config/update/', data);

// ════════════════════════════════════════════════════════════
//  Exam Pins
// ════════════════════════════════════════════════════════════
export const getAdminExamPins          = ()         => adminClient.get('/admin/exam-pins/');
export const getAdminExamDetails       = (exam)     => adminClient.get(`/admin/exam-pins/?exam=${exam}`);
export const postUpdateExamPin         = (data)     => adminClient.post('/admin/exam-pin/update/', data);
export const getExamPinInventory       = (provider) => adminClient.get(`/admin/exam-pins/?exam=${provider}`);
export const postAddExamPinInventory   = (data)     => adminClient.post('/admin/exam-pin/add/', data);
export const postMarkExamPinPurchased  = (data)     => adminClient.post('/admin/exam-pin/mark-purchased/', data);

// ════════════════════════════════════════════════════════════
//  Data Type Mappings
// ════════════════════════════════════════════════════════════
export const getDataMapping        = ()     => adminClient.get('/admin/data-mapping/');
export const postUpdateDataMapping = (data) => adminClient.post('/admin/data-mapping/update/', data);

// ════════════════════════════════════════════════════════════
//  Account (admin own profile)
// ════════════════════════════════════════════════════════════
export const getAdminAccount        = ()     => adminClient.get('/admin/account/');
export const postUpdateAdminAccount = (data) => adminClient.post('/admin/account/update/', data);
export const postUpdateAdminPin     = (data) => adminClient.post('/admin/account/update-pin/', data);

// ════════════════════════════════════════════════════════════
//  API Slots
// ════════════════════════════════════════════════════════════
export const getApiSlots         = ()     => adminClient.get('/admin/api-slots/');
export const postAddApiSlot      = (data) => adminClient.post('/admin/api-slot/add/', data);
export const postUpdateApiSlot   = (data) => adminClient.post('/admin/api-slot/update/', data);
export const postDeleteApiSlot   = (data) => adminClient.post('/admin/api-slot/delete/', data);
export const postToggleApiSlot   = (data) => adminClient.post('/admin/api-slot/toggle/', data);

// ════════════════════════════════════════════════════════════
//  Network Provider Slots (Data ID)
// ════════════════════════════════════════════════════════════
export const getNetworkProviderSlots      = ()     => adminClient.get('/admin/network-provider-slots/');
export const postSaveNetworkProviderSlots = (data) => adminClient.post('/admin/network-provider-slots/save/', data);

// ════════════════════════════════════════════════════════════
//  Data Plan Slot IDs
// ════════════════════════════════════════════════════════════
export const getDataPlanSlotIds      = (planId) => adminClient.get(`/admin/data-plan-slot-ids/?plan_id=${planId}`);
export const postSaveDataPlanSlotIds = (data)   => adminClient.post('/admin/data-plan-slot-ids/save/', data);

// ════════════════════════════════════════════════════════════
//  PocketFi Configuration
// ════════════════════════════════════════════════════════════
export const getPocketFiConfig        = ()     => adminClient.get('/admin/pocketfi-config/');
export const postUpdatePocketFiConfig = (data) => adminClient.post('/admin/pocketfi-config/update/', data);

// ════════════════════════════════════════════════════════════
//  FAQs
// ════════════════════════════════════════════════════════════
export const getFaqs        = ()     => adminClient.get('/admin/faqs/');
export const postAddFaq     = (data) => adminClient.post('/admin/faq/add/', data);
export const postUpdateFaq  = (data) => adminClient.post('/admin/faq/update/', data);
export const postDeleteFaq  = (data) => adminClient.post('/admin/faq/delete/', data);

// ════════════════════════════════════════════════════════════
//  Blacklist
// ════════════════════════════════════════════════════════════
export const getBlacklist        = (limit = 500) => adminClient.get(`/admin/blacklist/?limit=${limit}`);
export const postAddBlacklist    = (data)        => adminClient.post('/admin/blacklist/add/', data);
export const postDeleteBlacklist = (data)        => adminClient.post('/admin/blacklist/delete/', data);

// ════════════════════════════════════════════════════════════
//  Coupons
// ════════════════════════════════════════════════════════════
export const getCoupons       = ()     => adminClient.get('/admin/coupons/');
export const postAddCoupon    = (data) => adminClient.post('/admin/coupon/add/', data);
export const postDeleteCoupon = (data) => adminClient.post('/admin/coupon/delete/', data);

// ════════════════════════════════════════════════════════════
//  User Stats
// ════════════════════════════════════════════════════════════
export const getReferralContest  = (limit = 10000) => adminClient.get(`/admin/referral-contest/?limit=${limit}`);
export const getTopReferrals     = (limit = 10000) => adminClient.get(`/admin/top-referrals/?limit=${limit}`);
export const getTopFunding       = (limit = 10)    => adminClient.get(`/admin/top-funding/?limit=${limit}`);
export const getTopWalletUsers   = (limit = 10)    => adminClient.get(`/admin/top-wallet-users/?limit=${limit}`);
export const getSalesAnalysis    = (params)        => adminClient.get('/admin/sales-analysis/', { params });
export const getMonthlyFunding   = (limit = 10)    => adminClient.get(`/admin/monthly-funding/?limit=${limit}`);

// ════════════════════════════════════════════════════════════
//  Self / Custom API Integrations
// ════════════════════════════════════════════════════════════
export const getCustomApis        = ()     => adminClient.get('/admin/custom-apis/');
export const postAddCustomApi     = (data) => adminClient.post('/admin/custom-api/add/', data);
export const postUpdateCustomApi  = (data) => adminClient.post('/admin/custom-api/update/', data);
export const postDeleteCustomApi  = (data) => adminClient.post('/admin/custom-api/delete/', data);
export const postToggleCustomApi  = (data) => adminClient.post('/admin/custom-api/toggle/', data);
export const postTestCustomApi    = (data) => adminClient.post('/admin/custom-api/test/', data);

export default adminClient;
// ════════════════════════════════════════════════════════════
//  Update & License
// ════════════════════════════════════════════════════════════
export const getCheckUpdate  = ()     => adminClient.get('/admin/check-update/');
export const postRunUpdate   = ()     => adminClient.post('/admin/run-update/', {});
export const postSaveLicense = (data) => adminClient.post('/admin/save-license/', data);
